import React, { useState } from "react";
import {
  Box,
  Stack,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  IconButton,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DescriptionIcon from "@mui/icons-material/Description";
import EditIcon from "@mui/icons-material/Edit";
import CircleIcon from "@mui/icons-material/Circle";
import { AIReviewedChip } from "./RFPSummary";
import { ErrorRounded } from "@mui/icons-material";
import { Feedback } from "../../Components/Feedback";

const COMPLETENESS_ITEMS = [
  "Group size validated",
  "Class structure confirmed",
  "Coverage types identified",
  "Effective date provided",
  "Claims history reviewed",
];

// ------------------------- NEW RISK ITEMS -------------------------
const RISK_ITEMS = [
  "1099 employees identified — Payroll file shows 14 temporary/seasonal workers classified as contractors, creating eligibility and policy compliance concerns for Life coverage.",

  "PEO / Administrative Services involvement — Group uses ADP TotalSource for payroll administration, which may introduce complexity in confirming eligibility and ensuring W-2 status.",

  "Medium-risk industry (Retail / Warehousing) — Retail staff and warehouse pickers have an 18% higher accidental-death probability than standard office occupations.",

  "Years-in-business / location stability concern — This Walmart location opened in 2021, giving it only four years of operational history, increasing enrollment fluctuation exposure.",

  "Unusually high coverage request (GI expectations) — Broker requested a $150,000 GI for Basic Life, higher than standard GI caps for a mid-risk retail group of this size.",
];

// -----------------------------------------------------------------

const StepTwoScreen = ({ NextStep, apiResponse, rfpFile, BASE_URL }) => {
  const rfpCompleteness = apiResponse.completeness;
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        py: 3,
        px: 2,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "95vw", maxWidth: 1500 }}>
        <Stack spacing={3}>
          <RfpCompletenessSummaryCard
            rfpCompleteness={rfpCompleteness}
            rfpFile={rfpFile}
            BASE_URL={BASE_URL}
          />
          <RiskItemsCard
            rfpCompleteness={apiResponse}
            rfpFile={rfpFile}
            BASE_URL={BASE_URL}
          />
          <FlaggedItemsReviewCard
            NextStep={NextStep}
            rfpCompleteness={apiResponse?.rfp_summary}
            rfpFile={rfpFile}
            BASE_URL={BASE_URL}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default StepTwoScreen;

/* --------------------------------------------------------- */
/*  COMPLETENESS SUMMARY                                     */
/* --------------------------------------------------------- */

const RfpCompletenessSummaryCard = ({ rfpCompleteness, rfpFile, BASE_URL }) => {
  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CheckCircleRoundedIcon color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            RFP Completeness Summary
          </Typography>
          {/* AI Reviewed chip */}
          <AIReviewedChip />
        </Stack>

        <Box
          sx={{
            borderRadius: 3,
            bgcolor: "action.hover",
            overflow: "hidden",
          }}
        >
          {rfpCompleteness.rfp_completeness_items?.map((text, idx) => (
            <Stack
              key={idx}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: 3,
                py: 1.5,
                borderBottom:
                  idx === rfpCompleteness.rfp_completeness_items?.length - 1
                    ? "none"
                    : "1px solid",
                borderColor: "divider",
              }}
            >
              {text.status === "YES" ? (
                <CheckCircleRoundedIcon
                  sx={{ color: "success.main", fontSize: 20 }}
                />
              ) : (
                <ErrorRounded sx={{ color: "error.main", fontSize: 20 }} />
              )}
              <Typography variant="body2">{text.criterion}</Typography>
            </Stack>
          ))}
        </Box>

        {/* ✅ Feedback: Completeness Summary */}
        <Feedback
          rfpFile={rfpFile}
          BASE_URL={BASE_URL}
          field_path="completeness.rfp_completeness_items.criterion"
        />
      </CardContent>
    </Card>
  );
};

/* --------------------------------------------------------- */
/*  RISK ITEMS CARD                                          */
/* --------------------------------------------------------- */

const RiskItemsCard = ({ rfpCompleteness, rfpFile, BASE_URL }) => {
  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <WarningAmberIcon color="warning" fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Risk Evaluation Items
          </Typography>
          {/* AI Reviewed chip */}
          <AIReviewedChip />
        </Stack>

        <Box
          sx={{
            borderRadius: 3,
            bgcolor: "action.hover",
            overflow: "hidden",
          }}
        >
          {rfpCompleteness.risk_evaluation_items?.map((text, idx) => (
            <Stack
              key={idx}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                px: 3,
                py: 1.5,
                borderBottom:
                  idx === rfpCompleteness.risk_evaluation_items?.length - 1
                    ? "none"
                    : "1px solid",
                borderColor: "divider",
              }}
            >
              <WarningAmberIcon
                sx={{
                  color: "warning.main",
                  fontSize: 20,
                  alignSelf: "flex-start",
                }}
              />
              <Typography variant="body2">
                {text["title-description"]}
              </Typography>
            </Stack>
          ))}
        </Box>

        {/* ✅ Feedback: Risk Evaluation Items */}
        <Feedback
          rfpFile={rfpFile}
          BASE_URL={BASE_URL}
          field_path="risk_evaluation_items"
        />
      </CardContent>
    </Card>
  );
};

/* --------------------------------------------------------- */
/*  EDITABLE FIELD COMPONENT                                 */
/* --------------------------------------------------------- */

const EditableField = ({ label, initialValue }) => {
  const [value, setValue] = useState(initialValue);
  const [editing, setEditing] = useState(false);

  const handleBlur = () => setEditing(false);

  return (
    <Box sx={{ minWidth: 0 }}>
      {label && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5 }}
        >
          {label}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        {editing ? (
          <TextField
            variant="standard"
            size="small"
            autoFocus
            fullWidth
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleBlur}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, cursor: "text", minWidth: 80 }}
            onClick={() => setEditing(true)}
          >
            {value || "—"}
          </Typography>
        )}

        <IconButton
          size="small"
          onClick={() => setEditing(true)}
          sx={{ mt: editing ? 0.3 : 0 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

/* --------------------------------------------------------- */
/*  FLAGGED ITEMS REVIEW                                     */
/* --------------------------------------------------------- */

const FlaggedItemsReviewCard = ({
  NextStep,
  rfpCompleteness,
  rfpFile,
  BASE_URL,
}) => {
  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <DescriptionIcon color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Flagged Items for Review
          </Typography>
          {/* AI Reviewed chip */}
          <AIReviewedChip />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review and edit the following fields as needed:
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 3, md: 6 }}
          sx={{ mb: 3 }}
        >
          <Stack spacing={3} sx={{ flex: 1 }}>
            <EditableField
              label="Effective Date"
              initialValue={
                rfpCompleteness?.flagged_items_for_review?.effective_date
              }
            />
            <EditableField
              label="Claims History"
              initialValue={
                rfpCompleteness?.flagged_items_for_review?.claims_history
              }
            />
          </Stack>

          <Stack spacing={3} sx={{ flex: 1 }}>
            <EditableField
              label="Current Carrier"
              initialValue={
                rfpCompleteness?.flagged_items_for_review.current_carrier
              }
            />
            <EditableField
              label="Employee Contribution"
              initialValue={
                rfpCompleteness?.flagged_items_for_review?.employer_contribution
              }
            />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 1, mt: 1 }}
        >
          <WarningAmberIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
          <Typography
            variant="body2"
            sx={{ display: "flex", alignItems: "flex-end", fontWeight: 500 }}
          >
            {rfpCompleteness?.flagged_items_for_review?.summary_text}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircleIcon sx={{ fontSize: 6, mt: 0.6, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            Changes here will be used in subsequent workflow steps.
          </Typography>
        </Stack>

        {/* ✅ Feedback: Flagged Items for Review */}
        <Feedback
          rfpFile={rfpFile}
          BASE_URL={BASE_URL}
          field_path="rfp_summary.flagged_items_for_review"
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              NextStep();
            }}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 999, px: 3, ml: 2, mt: 1 }}
          >
            Proceed For Carrier Translation
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
